import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchAdminCheckoutRequests } from "@/admin/api";
import type { AdminCheckoutRequest } from "@/admin/types";

const formatDate = (value: string, locale: string) => {
  return new Intl.DateTimeFormat(locale === "ar" ? "ar-SA" : "en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
};

const getStatusLabelKey = (status: string) => {
  if (status === "salla_order_created") return "adminOrderStatusStoreOrderCreated";
  if (status === "pending") return "adminOrderStatusPending";
  if (status === "completed") return "adminOrderStatusCompleted";
  if (status === "cancelled") return "adminOrderStatusCancelled";
  return "adminOrderStatusUnknown";
};

export default function AdminOrders() {
  const { t, i18n } = useTranslation("common");
  const [requests, setRequests] = useState<AdminCheckoutRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const locale = i18n.language || "ar";

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setError("");

    fetchAdminCheckoutRequests()
      .then((data) => {
        if (!isMounted) return;
        setRequests(data);
      })
      .catch((err) => {
        console.error(err);
        if (!isMounted) return;
        setError(t("adminOrdersLoadFailed"));
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [t]);

  const totals = useMemo(() => {
    const storeOrders = requests.filter((request) => request.type === "store_order").length;
    return {
      all: requests.length,
      storeOrders,
      internalRequests: requests.length - storeOrders,
    };
  }, [requests]);

  if (isLoading) {
    return <div className="p-8 text-center text-gray-500">{t("loading")}</div>;
  }

  return (
    <div className="space-y-6" dir={i18n.dir()}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <p className="text-xs text-gray-400">{t("adminOrdersTotal")}</p>
          <p className="text-2xl font-bold text-navy-400 mt-2">{totals.all}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <p className="text-xs text-gray-400">{t("adminOrdersStoreOrders")}</p>
          <p className="text-2xl font-bold text-teal-600 mt-2">{totals.storeOrders}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <p className="text-xs text-gray-400">{t("adminOrdersInternalRequests")}</p>
          <p className="text-2xl font-bold text-primary-500 mt-2">{totals.internalRequests}</p>
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-2xl border border-red-100 bg-red-50 text-red-600 text-sm">
          {error}
        </div>
      )}

      {requests.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center text-gray-400">
          {t("adminOrdersNoData")}
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => (
            <article key={request.id} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
              <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      request.type === "store_order"
                        ? "bg-teal-50 text-teal-700"
                        : "bg-primary-50 text-primary-600"
                    }`}>
                      {request.type === "store_order" ? t("adminOrderTypeStore") : t("adminOrderTypeInternal")}
                    </span>
                    <span className="px-3 py-1 rounded-full bg-gray-50 text-gray-500 text-xs">
                      {t(getStatusLabelKey(request.status))}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-navy-400 mt-3">
                    {request.customerName || t("adminOrdersUnknownCustomer")}
                  </h3>
                  <p className="text-xs text-gray-400 mt-1">
                    {formatDate(request.createdAt, locale)}
                  </p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <p className="text-[11px] text-gray-400">{t("adminOrdersAmount")}</p>
                    <p className="font-bold text-navy-400">{request.totalAmount || "0"} {t("sar")}</p>
                  </div>
                  <div>
                    <p className="text-[11px] text-gray-400">{t("adminOrdersItems")}</p>
                    <p className="font-bold text-navy-400">{request.itemsCount}</p>
                  </div>
                  <div>
                    <p className="text-[11px] text-gray-400">{t("adminOrdersPhone")}</p>
                    <p className="font-bold text-navy-400">{request.customerPhone || "-"}</p>
                  </div>
                  <div>
                    <p className="text-[11px] text-gray-400">{t("adminOrdersStoreOrderNumber")}</p>
                    <p className="font-bold text-navy-400">{request.sallaOrderReferenceId || request.sallaOrderId || "-"}</p>
                  </div>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                {(request.products || []).map((product) => (
                  <div key={`${request.id}-${product.sallaProductId}`} className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 p-3">
                    {product.imageUrl ? (
                      <img src={product.imageUrl} alt="" className="w-14 h-14 rounded-xl object-cover bg-white" />
                    ) : (
                      <span className="w-14 h-14 rounded-xl bg-white flex items-center justify-center text-gray-300">
                        <i className="ri-image-line text-xl" />
                      </span>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-navy-400 truncate">{product.name || product.sallaProductId}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {product.quantity} x {product.price || 0} {t("sar")}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {request.storeOrderAdminUrl && (
                  <a href={request.storeOrderAdminUrl} target="_blank" rel="noopener noreferrer" className="px-4 py-2 rounded-xl bg-navy-400 text-white text-xs font-semibold hover:bg-navy-500">
                    {t("adminOrdersOpenInDashboard")}
                  </a>
                )}
                {request.storeOrderCustomerUrl && (
                  <a href={request.storeOrderCustomerUrl} target="_blank" rel="noopener noreferrer" className="px-4 py-2 rounded-xl bg-gray-50 text-navy-400 border border-gray-100 text-xs font-semibold hover:bg-gray-100">
                    {t("adminOrdersOpenCustomerLink")}
                  </a>
                )}
                {request.storeOrderCheckoutUrl && (
                  <a href={request.storeOrderCheckoutUrl} target="_blank" rel="noopener noreferrer" className="px-4 py-2 rounded-xl bg-primary-50 text-primary-600 border border-primary-100 text-xs font-semibold hover:bg-primary-100">
                    {t("adminOrdersOpenPaymentLink")}
                  </a>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
